// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import rfdc from 'rfdc';
import { AgGridUtils, CSVUtils } from '@cosmotech/core';
import { DATASET_ID_VARTYPE } from '../services/config/ApiConstants';
import WorkspaceService from '../services/workspace/WorkspaceService';
import { DatasetsUtils } from '../utils/DatasetsUtils';

const clone = rfdc();

const _buildDynamicParameterForUpdate = (parametersMetadata, parameterValues, parameterId) => {
  const varType = parametersMetadata[parameterId].varType;
  const parameterValue = parameterValues[parameterId];
  const value = varType === DATASET_ID_VARTYPE ? parameterValue?.id : parameterValue;
  return {
    parameterId,
    varType,
    value,
  };
};

const buildDynamicParametersToUpdate = (
  parametersMetadata,
  parameterValues,
  dynamicParametersIds,
  scenarioParametersValues,
  runTemplateParametersIds
) => {
  let parameters = [];
  const savedParametersValues = scenarioParametersValues?.filter(
    (parameter) =>
      !runTemplateParametersIds.includes(parameter.parameterId) &&
      !Object.keys(parameterValues).includes(parameter.parameterId)
  );
  for (const parameterId of dynamicParametersIds) {
    const parameter = _buildDynamicParameterForUpdate(parametersMetadata, parameterValues, parameterId);
    if (parameter.value != null) {
      parameters = parameters.concat(parameter);
    }
  }
  return [...parameters, ...savedParametersValues];
};

const buildQueryString = (value) => {
  return 'MATCH (n: `' + value + '`) RETURN n';
};

const buildMetadataQueryString = (value) => {
  return 'MATCH (n: `' + value + '`) RETURN n.Metadata as Metadata, n.Values as Values';
};

const buildClasseActifQueryString = (value) => {
  return `
    MATCH (n:\`${value}\`)
    WITH n, keys(n) AS attrs
    UNWIND attrs AS attr
    WITH COLLECT(DISTINCT attr) AS Attrs, COLLECT(DISTINCT n.NiveauCout) AS NiveauCout
    RETURN Attrs, NiveauCout
    `;
};

const buildReducedClasseActifQueryString = (value) => {
  // for new asset table format: extract AvailableColumns and NiveauCout from Metadata/Values rows
  return `
    MATCH (n:\`${value}\`)
    WITH collect(n) as nodes
    WITH
      [x IN nodes WHERE x.Metadata = 'AvailableColumns' | x.Values][0] AS Attrs,
      [x IN nodes WHERE x.Metadata = 'NiveauCout' | x.Values][0] AS NiveauCout
    RETURN Attrs, NiveauCout
  `;
};
const isValueTruthy = (value) => {
  return value === true || value === 'true';
};

const _buildRows = (rows, hasHeader, cols) => {
  const formattedData = [];
  const startIndex = hasHeader ? 1 : 0;
  for (let rowIndex = startIndex; rowIndex < rows.length; rowIndex++) {
    const row = {};
    cols.forEach((col) => {
      const colIndex = rows[0].findIndex((el) => el === col.field);
      if (colIndex !== -1) {
        row[col.field] = rows[rowIndex][colIndex];
      } else {
        row[col.field] = undefined;
      }
    });
    formattedData.push(row);
  }
  return formattedData;
};

const filterSpecificAttributes = (attributDeclencheur) => {
  const attrsToFilter = ['NiveauCout', 'CategoriesRH', 'CategoriesBudget'];
  const attrsToFilterDynamic = ['CustomDataString', 'CustomDataList'];
  return (
    attrsToFilter.includes(attributDeclencheur) ||
    attrsToFilterDynamic.some((attr) => attributDeclencheur.startsWith(attr))
  );
};

const getFilteredMetadata = (valuesData) => {
  const filteredValues = valuesData.filter((element) => filterSpecificAttributes(element.Metadata));
  const valuesDataDict = {};
  filteredValues.forEach((element) => {
    valuesDataDict[element.Metadata] = element.Values;
  });
  return valuesDataDict;
};

const getMaxCharWidth = (colCount) => {
  const colWidth = 100 / (colCount + 1);
  if (colWidth >= 20) return '100ch';
  if (colWidth >= 15) return '70ch';
  if (colWidth >= 10) return '40ch';
  return '20ch';
};

const parseAssetClassCsvFile = (baseValue, fileContent) => {
  const fileContentFirstLine = fileContent.split('\n')[0].split(',');
  const prevColumns = baseValue?.map((row) => Object.keys(row)).flat() ?? [];
  const columnsNames = Array.from(new Set([...prevColumns, ...fileContentFirstLine])).map((name) => name.trim());
  const columns = columnsNames.map((name) => ({ field: name, acceptsEmptyFields: true }));
  const newValue = _buildRows(CSVUtils.read(fileContent), true, columns);
  const result = clone(baseValue);
  const acceptedIds = baseValue?.map((value) => value.Identifiant);
  const filteredNewValue = newValue.filter((line) => acceptedIds.includes(line.Identifiant));
  result?.forEach((value) => {
    const correspondingValue = filteredNewValue.find((line) => line.Identifiant === value.Identifiant);
    columnsNames.forEach((column) => {
      if (correspondingValue?.[column] != null && correspondingValue?.[column] !== '') {
        value[column] = correspondingValue?.[column];
      } else value[column] = value[column] ?? '';
    });
  });
  return result;
};

// retrieves column names from all rows as redis doesn't return column name if it's value is null
// and getting column names from the first line only can lead to a data loss in following lines
const getAllUniqueColumnsNames = (rows) => {
  return Array.from(new Set(rows?.map((row) => Object.keys(row)).flat()));
};
const getAllUniqueColumns = (rows) => {
  const columnNames = getAllUniqueColumnsNames(rows);
  return columnNames?.map((col) => ({
    field: col,
    acceptsEmptyFields: true,
  }));
};

function buildEquipmentFamiliesMapping(hierarchieDesEquipementsList) {
  const equipmentFamiliesMapping = {};
  function collectDescendantFamilies(currentKey, allItems) {
    const families = new Set();

    for (const item of allItems) {
      const parents = (item.Parents ?? '').split('|');
      if (parents.includes(currentKey)) {
        if (item.TypeNiveau === 'Equipement') {
          const itemFamilies = item.FamillesEquipement?.split('|') ?? [];
          itemFamilies.forEach((f) => families.add(f));
        }
        const childFamilies = collectDescendantFamilies(item.NiveauHierarchique, allItems);
        childFamilies.forEach((f) => families.add(f));
      }
    }

    return families;
  }
  hierarchieDesEquipementsList.forEach((item) => {
    if (item.TypeNiveau === 'Equipement') {
      const familles = item.FamillesEquipement?.split('|') ?? [];
      equipmentFamiliesMapping[item.NiveauHierarchique] = familles;
    }
  });
  hierarchieDesEquipementsList
    .filter((item) => item.TypeNiveau === 'Groupe')
    .forEach((groupItem) => {
      const uniqueFamilies = collectDescendantFamilies(groupItem.NiveauHierarchique, hierarchieDesEquipementsList);
      equipmentFamiliesMapping[groupItem.NiveauHierarchique] = [...uniqueFamilies];
    });

  return equipmentFamiliesMapping;
}

const createFileContent = (rows) => {
  if (!rows || rows.length === 0) return '';
  const columns = getAllUniqueColumns(rows);
  return AgGridUtils.toCSV(rows, columns);
};
const getColumnsFormatedForAgGridUtils = (columns) => {
  const filteredColumns = columns?.filter((col) => col.inputType !== 'button').map((col) => col.field ?? col);
  if (filteredColumns && Array.isArray(filteredColumns)) {
    return filteredColumns.map((col) => ({
      field: col,
      acceptsEmptyFields: true,
    }));
  }
};
const generateDefaultCoutsEtDurees = (niveauDefinition, identifiantAction, familleEquipement, niveauCout) => {
  if (!identifiantAction || !niveauDefinition) return [];

  if (niveauDefinition === 'Action') {
    return niveauCout.map((niveau) => ({
      id: `cout-${Math.random().toString(36).substring(2, 9)}`,
      IdentifiantAction: identifiantAction,
      NiveauCouts: niveau,
      TypeCout: 'Strict',
      CoutUnitaire: 0,
      CoutFixe: 0,
      DureeUnitaire: 0,
      DureeFixe: 1,
      FamilleEquipement: null,
    }));
  }

  if (niveauDefinition === 'SubAction') {
    return familleEquipement.flatMap((fam) =>
      niveauCout.map((niveau) => ({
        id: `cout-${Math.random().toString(36).substring(2, 9)}`,
        IdentifiantAction: identifiantAction,
        NiveauCouts: niveau,
        TypeCout: 'Strict',
        CoutUnitaire: 0,
        CoutFixe: 0,
        DureeUnitaire: 0,
        DureeFixe: 1,
        FamilleEquipement: fam.label,
      }))
    );
  }

  return [];
};

const generateDefaultImpacts = (identifiantAction, familleEquipement) => {
  if (!identifiantAction) return [];

  return familleEquipement.map((famille) => ({
    id: `impact-${Math.random().toString(36).substring(2, 9)}`,
    IdentifiantAction: identifiantAction,
    FamilleEquipementImpactee: famille.label,
    Type: 'NoAction',
    Impact: 0,
    ImpactPriorisation: false,
    FamilleEquipementRetrofit: null,
  }));
};

async function downloadFileData(organizationId, workspaceId, datasets, fileValue) {
  const dataset = datasets?.find((dataset) => dataset.id === fileValue);
  const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
  const data = await WorkspaceService.downloadWorkspaceFileData(organizationId, workspaceId, storageFilePath);
  const fileContentFirstLine = data.split('\n')[0].split(',');
  const columns = fileContentFirstLine.map((col) => ({ field: col, acceptsEmptyFields: true }));
  const parsedData = AgGridUtils.fromCSV(data, true, columns);
  return parsedData.rows;
}

const sortResourceListAlphabetically = (list) => {
  if (!list) return [];
  return list
    .filter((item) => item != null)
    .map((item) => String(item))
    .sort((a, b) => a.localeCompare(b));
};

const isValidCriterionName = (criterionName) => !criterionName.match(/[^a-zA-Z0-9_<>=.\-+]+/i);

const findIndexOfMatchingClosingParenthesis = (conditionString, openingParenthesisIndex) => {
  let openedParenthesesCount = 1;
  let index = openingParenthesisIndex + 1;
  for (; index < conditionString.length; ++index) {
    if (conditionString[index] === '(') ++openedParenthesesCount;
    else if (conditionString[index] === ')') --openedParenthesesCount;

    if (openedParenthesesCount === 0) break;
  }
  return index;
};

const findEndIndexOfCriterionInString = (conditionString, startIndex = 0) => {
  const firstSpaceIndex = conditionString.indexOf(' ', startIndex);
  const firstParenthesisIndex = conditionString.indexOf('(', startIndex);

  if (firstSpaceIndex === -1 && firstParenthesisIndex === -1) return conditionString.length;
  if (firstSpaceIndex === -1) return firstParenthesisIndex;
  if (firstParenthesisIndex === -1) return firstSpaceIndex;
  return firstSpaceIndex < firstParenthesisIndex ? firstSpaceIndex : firstParenthesisIndex;
};

const generateRandomId = () => Math.random().toString(36).substring(2, 9);

// For the criteria formula editor, unique ids are necessary to keep track of elements in the condition tree when
// other elements are removed or inserted (using list indices would not be reliable). This function patches the
// provided condition tree object to recursively add, in all the elements of the tree, a property "uid" with a
// random (hopefully unique) id
const patchConditionTreeWithUID = (conditionTree) => {
  if (!conditionTree) return;

  if (!conditionTree.uid) conditionTree.uid = generateRandomId();
  if (conditionTree.children)
    conditionTree.children.forEach((conditionTreeItem) => patchConditionTreeWithUID(conditionTreeItem));
};

// Finds operands, operator and endIndex of a group in a condition string
const parseConditionGroupFromString = (rawConditionString) => {
  if (!rawConditionString) throw new Error(`Empty or undefined condition string: "${rawConditionString}"`);

  let conditionString = rawConditionString.trim();
  const operands = [];
  let operator;

  let processingIndex = 0;
  while (conditionString.length > 0) {
    if (conditionString[processingIndex] === '(') {
      // Process sub-groups (between parentheses)
      const subGroupEndIndex = findIndexOfMatchingClosingParenthesis(conditionString, processingIndex);
      const subGroupConditionString = conditionString.slice(processingIndex + 1, subGroupEndIndex);
      operands.push(parseConditionGroupFromString(subGroupConditionString));
      conditionString = conditionString.slice(subGroupEndIndex + 1).trim();
    } else {
      // Process first criterion extracted from the string
      const criterionEndIndex = findEndIndexOfCriterionInString(conditionString, processingIndex);
      const criterionConditionString = conditionString.slice(processingIndex, criterionEndIndex);
      if (!isValidCriterionName(criterionConditionString))
        throw new Error(`Invalid criterion name in condition string: "${rawConditionString}"`);

      operands.push({ type: 'criterion', id: criterionConditionString });
      conditionString = conditionString.slice(criterionEndIndex + 1).trim();
    }

    if (conditionString.length === 0) break;
    processingIndex = 0;

    // A group operand has been extracted, there should be an operator next
    let newOperatorFound;
    if (conditionString.startsWith('AND')) newOperatorFound = 'and';
    else if (conditionString.startsWith('OR')) newOperatorFound = 'or';

    if (newOperatorFound == null) throw new Error(`Non-terminated condition string: "${rawConditionString}"`);
    else if (operator != null && newOperatorFound != null && operator !== newOperatorFound) {
      throw new Error(`Condition string mixes AND and OR operators: "${rawConditionString}"`);
    } else if (operator == null) operator = newOperatorFound.toLowerCase();

    processingIndex += newOperatorFound.length;
    conditionString = conditionString.slice(processingIndex + 1).trim();
    processingIndex = 0;
  }

  // Group parsing finished, simplify the object if possible
  if (operands.length === 1) {
    if (operator != null)
      throw new Error(`Missing operand after operator in condition string: "${rawConditionString}"`);
    return operands[0];
  }

  return { children: operands, type: operator };
};

const createTriggerConditionTreeFromString = (conditionString) => {
  if (typeof conditionString !== 'string') {
    console.warn(`Invalid type for condition string "${conditionString}"`);
    return {};
  }

  const openingParenthesesCount = conditionString.match(/\(/g)?.length ?? 0;
  const closingParenthesesCount = conditionString.match(/\)/g)?.length ?? 0;
  if (openingParenthesesCount !== closingParenthesesCount) {
    console.warn(`Opening and closing parentheses do not match in condition string "${conditionString}"`);
    return {};
  }

  try {
    const condition = parseConditionGroupFromString(conditionString);
    // Make sure we have at least one group with "AND" condition
    if (condition.type !== 'criterion') return condition;
    return { type: 'and', children: [condition] };
  } catch (e) {
    console.warn(e);
    return {};
  }
};

const createTriggerConditionStringFromTree = (conditionTree, isRootCondition = true) => {
  const getErrorValue = () => (isRootCondition ? '' : null);

  if (conditionTree == null || typeof conditionTree !== 'object' || Array.isArray(conditionTree)) {
    console.warn(`Invalid type for condition tree object "${JSON.stringify(conditionTree)}"`);
    return getErrorValue();
  }

  // Exception: empty objects are valid values
  if (Object.keys(conditionTree).length === 0) return '()';

  const validTypes = ['and', 'or', 'criterion'];
  if (!validTypes.includes(conditionTree.type)) {
    console.warn(
      `Invalid condition type "${conditionTree.type}" for condition tree object "${JSON.stringify(conditionTree)}"`
    );
    return getErrorValue();
  }

  if (conditionTree.type === 'criterion') {
    if (conditionTree.id == null) {
      console.warn(`Missing criterion id in condition tree object "${JSON.stringify(conditionTree)}"`);
      return getErrorValue();
    }
    if (isRootCondition) {
      console.warn(
        `Criterion object cannot be the root of a condition tree object: "${JSON.stringify(conditionTree)}"`
      );
      return getErrorValue();
    }
    return conditionTree.id;
  }

  if (!Array.isArray(conditionTree.children)) {
    console.warn(
      `Missing or invalid condition "children" property in condition tree object "${JSON.stringify(conditionTree)}"`
    );
    return getErrorValue();
  }

  const serializedChildren = conditionTree.children.map((child) => createTriggerConditionStringFromTree(child, false));
  if (serializedChildren.includes(null)) return getErrorValue();

  const operator = conditionTree.type.toUpperCase();
  return '(' + serializedChildren.join(` ${operator} `) + ')';
};

// This function removes all criteria with the provided id from a condition tree. The conditionTree object will be
// modified.
// Returns: true if the tree has been modified, false otherwise
const removeCriterionIdFromConditionTree = (conditionTree, idOfCriterionToRemove) => {
  if (!conditionTree.children) return false; // The conditionTree object is a criterion, nothing to do

  // Filter criteria that are direct children of the current condition
  const previousChildrenCount = conditionTree.children.length;
  conditionTree.children = conditionTree.children.filter(
    (conditionTreeItem) => conditionTreeItem.id == null || conditionTreeItem.id !== idOfCriterionToRemove
  );

  let hasChanged = previousChildrenCount !== conditionTree.children.length;

  // Filter criteria that are inside condition blocks (AND or OR)
  conditionTree.children.forEach((conditionTreeItem) => {
    hasChanged = removeCriterionIdFromConditionTree(conditionTreeItem, idOfCriterionToRemove) || hasChanged;
  });
  return hasChanged;
};

// This function removes all criteria with the provided id from a condition string
const removeCriterionIdFromConditionString = (conditionString, idOfCriterionToRemove) => {
  const conditionTree = createTriggerConditionTreeFromString(conditionString);
  const hasChanged = removeCriterionIdFromConditionTree(conditionTree, idOfCriterionToRemove);

  if (!hasChanged) return conditionString;
  return createTriggerConditionStringFromTree(conditionTree);
};

// This function renames a given criterion in a condition tree. The conditionTree object will be modified.
// Returns: true if the tree has been modified, false otherwise
const renameCriterionInConditionTree = (conditionTree, previousCriterionId, newCriterionId) => {
  if (!conditionTree.children) return false; // The conditionTree object is a criterion, nothing to do

  // Rename criteria that are direct children of the current condition
  let hasChanged = false;
  conditionTree.children = conditionTree.children.map((conditionTreeItem) => {
    if (conditionTreeItem.id == null || conditionTreeItem.id !== previousCriterionId) return conditionTreeItem;

    hasChanged = true;
    conditionTreeItem.id = newCriterionId;
    return conditionTreeItem;
  });

  // Filter criteria that are inside condition blocks (AND or OR)
  conditionTree.children.forEach((conditionTreeItem) => {
    hasChanged = renameCriterionInConditionTree(conditionTreeItem, previousCriterionId, newCriterionId) || hasChanged;
  });
  return hasChanged;
};

// This function renames all occurences of the given criteria in a condition string
const renameCriterionInConditionString = (conditionString, previousCriterionId, newCriterionId) => {
  const conditionTree = createTriggerConditionTreeFromString(conditionString);
  const hasChanged = renameCriterionInConditionTree(conditionTree, previousCriterionId, newCriterionId);

  if (!hasChanged) return conditionString;
  return createTriggerConditionStringFromTree(conditionTree);
};

const isFormulaEmpty = (conditionString) =>
  conditionString == null || conditionString.replace(/\(/g, '').replace(/\)/g, '').trim().length === 0;

const findChildren = (hierarchyData, assetId) => {
  // Find direct children of the current asset
  const children = hierarchyData
    .filter((item) => {
      const parents = (item.Parents ?? '').split('|').filter((p) => p.trim() !== '');
      return parents.includes(assetId);
    })
    .map((item) => item.NiveauHierarchique || item.Id);

  const descendants = [];

  for (const child of children) {
    const childItem = hierarchyData.find((item) => item.NiveauHierarchique === child);
    if (childItem) {
      // Only add to descendants if TypeNiveau is not 'Groupe' (Group)
      if (childItem.TypeNiveau !== 'Groupe') {
        descendants.push(child);
      }
      descendants.push(...findChildren(hierarchyData, child));
    }
  }

  return [...new Set(descendants)];
};

export const NON_COMPARING_ATTRIBUTES = ['Failure', 'QuasiFailure', 'Operating', 'AllPredeterminedProjectsDone'];
export const NUMERICAL_NON_COMPARING_ATTRIBUTES = ['InspectionDowntime', 'Downtime'];

export const DEFAULT_NUMERIC_OPERATOR = 'GEQ';

const isOperateurDisabledForAttribut = (
  attributDeclencheur,
  nonComparingAttributes = NON_COMPARING_ATTRIBUTES.concat(NUMERICAL_NON_COMPARING_ATTRIBUTES)
) => {
  if (!attributDeclencheur) return false;
  return nonComparingAttributes.includes(attributDeclencheur) || attributDeclencheur.startsWith('CustomDataString.');
};

const isNumericComparingAttribute = (
  attributDeclencheur,
  nonComparingAttributes = NON_COMPARING_ATTRIBUTES.concat(NUMERICAL_NON_COMPARING_ATTRIBUTES)
) => {
  if (!attributDeclencheur) return false;
  return !isOperateurDisabledForAttribut(attributDeclencheur, nonComparingAttributes);
};

export const WizardUtils = {
  buildDynamicParametersToUpdate,
  buildQueryString,
  buildClasseActifQueryString,
  buildReducedClasseActifQueryString,
  isValueTruthy,
  parseAssetClassCsvFile,
  createFileContent,
  getAllUniqueColumns,
  getAllUniqueColumnsNames,
  downloadFileData,
  getColumnsFormatedForAgGridUtils,
  sortResourceListAlphabetically,
  buildEquipmentFamiliesMapping,
  buildMetadataQueryString,
  getFilteredMetadata,
  filterSpecificAttributes,
  generateDefaultImpacts,
  generateDefaultCoutsEtDurees,
  getMaxCharWidth,
  createTriggerConditionTreeFromString,
  createTriggerConditionStringFromTree,
  patchConditionTreeWithUID,
  removeCriterionIdFromConditionString,
  renameCriterionInConditionTree,
  renameCriterionInConditionString,
  isFormulaEmpty,
  findChildren,
  isOperateurDisabledForAttribut,
  isNumericComparingAttribute,
};
